/**
 * R-131-EPISTEMIC-GROUNDING-C — reader-safe lists: confirmed red flags vs open questions.
 *
 * Editorial conversion (findings-bop group): the section heads no longer
 * reach for literal text-red-600 / text-amber-700 / text-blue-700. Each
 * head is a small-caps mono label toned via roleColor(theme,…) —
 * confirmed red flags in the NEGATIVE tone, verification-pending and open
 * questions both in the ACCENT tone. (Verification-pending WAS the
 * 'warning' role = amber #d97706 on the default theme; retoned to 'accent'
 * to kill the "Open Questions ORANGE" off-theme heading William flagged —
 * QA 2026-06-10 — so all three findings heads track the active preset.)
 * The active theme threads
 * down to each FindingClaimCard so its left-rule + evidence badge follow
 * the preset. With no `theme` everything falls through to DEFAULT_THEME,
 * keeping legacy (no-preset) reports visually consistent.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import type { DataSource } from '../../../types/siteRag';
import type {
  FindingClaim,
  FindingClaimEntry,
  OpenQuestionAnswerValue,
  OpenQuestionResponse,
} from '../../../lib/findingClaim';
import {
  buildOpenQuestionQuestionKey,
  findOpenQuestionResponse,
  partitionFindingClaims,
  resolveOpenQuestionIdentity,
} from '../../../lib/findingClaim';
import type { ReportTheme } from '../../../types/reportTheme';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor, type ReportColorRole } from '../../../lib/reportRoleColors';
import { useViewMode } from '../viewMode';
import { useStore } from '../../../store';
import { auth } from '../../../firebase';
import { reportOwnerUid, siteOwnerUid } from '../../../lib/siteOwnerPath';
import { FindingClaimCard } from './FindingClaimCard';
import { hasImmutableReportIdentity } from '../reportPresentationMode';

export interface FindingClaimsListProps {
  entries: FindingClaimEntry[];
  references?: DataSource[];
  siteId?: string;
  reportId?: string;
  sectionId?: string;
  /** Responses persisted on the report; matched by report, section and question. */
  responses?: OpenQuestionResponse[];
  /** Heading for confirmed-only red flags (customer-safe). */
  redFlagsHeading?: string;
  showVerificationPending?: boolean;
  showOpenQuestions?: boolean;
  className?: string;
  /**
   * Active report theme — drives the editorial head tones + each card's
   * chrome. Optional: omitting it falls through to DEFAULT_THEME so
   * existing call sites keep a visually consistent look.
   */
  theme?: ReportTheme;
}

function SectionBlock({
  title,
  headColor,
  children,
  testId,
}: {
  title: string;
  headColor: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <div data-testid={testId}>
      <h4
        className="text-xs font-semibold uppercase tracking-wide"
        style={{
          color: headColor,
          fontFamily: 'var(--report-body-font, inherit)',
          letterSpacing: '0.12em',
        }}
      >
        {title}
      </h4>
      <ul className="mt-2 list-none space-y-2 pl-0">{children}</ul>
    </div>
  );
}

export function FindingClaimsList({
  entries,
  references = [],
  siteId,
  reportId,
  sectionId,
  responses = [],
  redFlagsHeading = 'Red flags',
  showVerificationPending = true,
  showOpenQuestions = true,
  className,
  theme: themeProp,
}: FindingClaimsListProps) {
  // No explicit theme ⇒ the EFFECTIVE theme (preset + reader-mode stamp), not
  // bare DEFAULT_THEME — see FindingClaimCard for the dark-reader rationale.
  const effectiveTheme = useEffectiveReportTheme();
  const theme = themeProp ?? effectiveTheme;
  const viewMode = useViewMode();
  const answerOpenQuestion = useStore(s => s.answerOpenQuestion);
  const site = useStore(s => siteId ? s.siteRag?.sites.find(candidate => candidate.id === siteId) : undefined);
  const report = useStore(s => reportId ? s.siteRag?.reports.find(candidate => candidate.id === reportId) : undefined);
  const [answeringKeys, setAnsweringKeys] = useState<Set<string>>(() => new Set());
  const [responseOverrides, setResponseOverrides] = useState<Record<string, OpenQuestionResponse>>({});

  const partitioned = partitionFindingClaims(entries);
  const currentUid = auth.currentUser?.uid;
  const allowAnswer = viewMode === 'editor'
    && Boolean(siteId && reportId && site && report && currentUid)
    && !hasImmutableReportIdentity(report ?? {})
    && siteOwnerUid(site, currentUid ?? '') === currentUid
    && reportOwnerUid(report, currentUid ?? '') === currentUid;

  const headColor = (role: ReportColorRole) => roleColor(theme, role);

  const responseKey = (claim: FindingClaim) => {
    const identity = resolveOpenQuestionIdentity(claim);
    return buildOpenQuestionQuestionKey({
      reportId: reportId ?? '',
      sectionId,
      question: identity.question,
      questionAliases: identity.questionAliases,
    });
  };

  const pagedControlId = (claim: FindingClaim) =>
    `open-question-${encodeURIComponent(responseKey(claim))}`;

  const handleAnswer = async (
    claim: FindingClaim,
    answer: OpenQuestionAnswerValue,
    details?: string,
  ): Promise<boolean> => {
    if (!siteId || !reportId) return false;
    const key = responseKey(claim);
    setAnsweringKeys((current) => new Set(current).add(key));
    try {
      const result = await answerOpenQuestion({
        siteId,
        openQuestion: claim,
        answer,
        details,
        reportId,
        sectionId,
      });
      // Only call a response report-recorded when its strict report write
      // succeeded. SiteContext may already contain a canonical attestation,
      // but showing an optimistic report overlay here would disappear on
      // reload and falsely imply that the report itself was durable.
      if (result.reportSaved) {
        setResponseOverrides(prev => ({ ...prev, [key]: result.response }));
      }

      if (
        result.siteSaved
        && result.reportSaved
        && result.sedSaved
        && result.response.sedSyncStatus === 'synced'
      ) {
        toast.success('Answer saved to report and SED');
      } else if (
        result.siteSaved
        && result.reportSaved
        && result.response.sedSyncStatus === 'pending'
      ) {
        toast.warning('Saved to the report and site context; SED sync needs retry');
      } else if (result.siteSaved && !result.reportSaved) {
        toast.warning('Saved to site context; report sync needs retry');
      } else {
        toast.warning('Response saved partially; one or more destinations need retry');
      }
      return result.reportSaved;
    } catch (err) {
      toast.error((err as Error).message);
      return false;
    } finally {
      setAnsweringKeys((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  const hasAny =
    partitioned.confirmed.length > 0
    || partitioned.verificationPending.length > 0
    || partitioned.openQuestions.length > 0;

  if (!hasAny) return null;

  return (
    <div className={className} data-testid="finding-claims-list">
      {partitioned.confirmed.length > 0 && (
        <SectionBlock title={redFlagsHeading} headColor={headColor('negative')} testId="finding-claims-confirmed">
          {partitioned.confirmed.map((claim, i) => (
            <li key={`confirmed-${i}`}>
              <FindingClaimCard claim={claim} references={references} theme={theme} />
            </li>
          ))}
        </SectionBlock>
      )}
      {showVerificationPending && partitioned.verificationPending.length > 0 && (
        <SectionBlock
          title="Verification pending"
          headColor={headColor('accent')}
          testId="finding-claims-verification-pending"
        >
          {partitioned.verificationPending.map((claim, i) => (
            <li key={`pending-${i}`}>
              <FindingClaimCard claim={claim} references={references} theme={theme} />
            </li>
          ))}
        </SectionBlock>
      )}
      {showOpenQuestions && partitioned.openQuestions.length > 0 && (
        <SectionBlock
          title="Open questions"
          headColor={headColor('accent')}
          testId="finding-claims-open-questions"
        >
          {partitioned.openQuestions.map((claim, i) => {
            const identity = resolveOpenQuestionIdentity(claim);
            const key = responseKey(claim);
            const response = responseOverrides[key]
              ?? (reportId
                ? findOpenQuestionResponse(responses, {
                    reportId,
                    sectionId,
                    question: identity.question,
                    questionAliases: identity.questionAliases,
                  })
                : undefined);
            return (
              <li key={`open-${i}`}>
                <FindingClaimCard
                  claim={claim}
                  references={references}
                  theme={theme}
                  showAnswerAffordance={allowAnswer}
                  answering={answeringKeys.has(key)}
                  response={response}
                  pagedControlId={pagedControlId(claim)}
                  onAnswer={(answer, details) => handleAnswer(claim, answer, details)}
                />
              </li>
            );
          })}
        </SectionBlock>
      )}
    </div>
  );
}
